import { useUnsavedGuard } from "./useUnsavedGuard";
import { LEVEL_LABEL, STAGE_LABEL } from "./vocabulary";
// Hand-tuned primitives shared by every screen (V1-INTERACTIONS §1.4–1.5): status pills, the phase pill, key-value blocks,
// scrollable tables, inline banners for refused/conflict/error states, the reason-and-confirm dialog, and form fields.
import { createContext, useContext, Children, cloneElement, isValidElement, useEffect, useLayoutEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AppError, getBusinessTimeZone } from "@/api/runtime";
import { ERROR_LABELS } from "@/mc/labels";
export { ObjectReadGuard } from "./ObjectReadGuard";

export function Pill({ tone, children, title, category = false }: { tone: "ok" | "warn" | "bad" | "info" | "muted"; children: ReactNode; title?: string; category?: boolean }) {
  return (
    <span className={`pill pill--${category ? "category" : tone}`} title={title}>
      {!category && !Children.toArray(children).some(child => isValidElement(child)) && <span className="pill__glyph" aria-hidden="true" />}
      {children}
    </span>
  );
}

export function CopyId({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  return <span className="copy-id"><span className="mono muted" title={value}>{value.length > 24 ? `${value.slice(0, 8)}…${value.slice(-7)}` : value}</span><button type="button" className="link" aria-label={`${copied ? "Copied" : "Copy"} ${value}`} onClick={() => void navigator.clipboard.writeText(value).then(() => { setCopied(true); setFailed(false); }).catch(() => setFailed(true))}>{copied ? "Copied" : "Copy"}</button>{copied && <span role="status" className="sr-only">Copied {value}</span>}{failed && <span role="status">Could not copy. Select the full value: {value}</span>}</span>;
}

export function Identity({ name, id, relation }: { name?: string | null | undefined; id?: string | null | undefined; relation?: string }) {
  return <span className="identity">{relation && <span className="identity__relation">{relation} </span>}{name || id || "System"}{name && id && <span className="identity__id"> · {id}</span>}</span>;
}

export const STATE_TONE: Record<string, "ok" | "warn" | "bad" | "info" | "muted"> = {
  on_track: "ok",
  trending_behind: "warn",
  behind: "bad",
  on_hold: "muted",
  not_started: "muted",
  closed_on_target: "ok",
  closed_behind: "bad",
};
export const STATE_LABEL: Record<string, string> = { on_track: "On track", trending_behind: "Trending behind", behind: "Behind", on_hold: "On hold", not_started: "Not started", closed_on_target: "Closed on target", closed_behind: "Closed behind" };

/** The one scorecard section bar owns its measured clearance, including after font/viewport changes. */
export function SectionBar({ children }: { children: ReactNode }) {
  const bar = useRef<HTMLElement>(null);
  const [current, setCurrent] = useState("");
  useLayoutEffect(() => {
    const element = bar.current;
    if (!element) return;
    const root = document.documentElement;
    const measure = () => root.style.setProperty("--secbar-height", `${element.getBoundingClientRect().height}px`);
    measure();
    const main = document.getElementById("main");
    const targets = () => [...element.querySelectorAll<HTMLButtonElement>("button[aria-controls]")].flatMap(button => {
      const target = document.getElementById(button.getAttribute("aria-controls")!);
      return target ? [target] : [];
    });
    const locate = () => {
      const sections = targets(), edge = element.getBoundingClientRect().bottom + 12;
      const active = sections.filter(section => section.getBoundingClientRect().top <= edge).at(-1) ?? sections[0];
      if (active) setCurrent(active.id);
    };
    const focus = (event: FocusEvent) => {
      const section = event.target instanceof Element ? event.target.closest("section[id]") : null;
      if (section && targets().includes(section as HTMLElement)) setCurrent(section.id);
    };
    const observer = new ResizeObserver(() => { measure(); locate(); }); observer.observe(element);
    main?.addEventListener("scroll", locate, { passive: true });
    document.addEventListener("focusin", focus);
    locate();
    return () => { observer.disconnect(); main?.removeEventListener("scroll", locate); document.removeEventListener("focusin", focus); root.style.removeProperty("--secbar-height"); };
  }, []);
  return <nav ref={bar} className="secbar" aria-label="Sections">{Children.map(children, child => {
    if (!isValidElement<{ "aria-controls"?: string; "aria-current"?: "true" | undefined }>(child) || !child.props["aria-controls"]) return child;
    return cloneElement(child, { "aria-current": current === child.props["aria-controls"] ? "true" : undefined });
  })}</nav>;

}

export const STATE_GLYPH: Record<string, string> = { on_track: "●", trending_behind: "◆", behind: "▲", on_hold: "‖", not_started: "○", closed_on_target: "●", closed_behind: "▲" };

export function PhasePill({ phase }: { phase: { label: string | null; elapsed_days: number | null; target_days: number | null; state: string; held_days: number } }) {
  if (!phase.label || phase.elapsed_days === null) return <NA />;
  const text = phase.target_days ? `day ${phase.elapsed_days} of ${phase.target_days}` : `day ${phase.elapsed_days}`;
  return (
    <span className="phase">
      <Pill tone={STATE_TONE[phase.state] ?? "muted"} title={phase.label}>
        <span aria-hidden="true">{STATE_GLYPH[phase.state] ?? "○"}</span><span className="sr-only">{STATE_LABEL[phase.state] ?? phase.state}: </span> {text}
        {phase.held_days > 0 ? ` · +${phase.held_days} held` : ""}
      </Pill>
      <span className="phase__label">{phase.label}</span>
    </span>
  );
}

export const MISSING = "N/A";
const INVALID_DATE = "Invalid date";
const INVALID_TIME = "Invalid time range";
const INVALID_AMOUNT = "Invalid amount";
const INVALID_VALUES = new Set([INVALID_DATE, INVALID_TIME, INVALID_AMOUNT]);
export function NA({ reason }: { reason?: string }) {
  return <span className={reason ? "na na--reason" : "na"} title={reason} aria-label={reason ? `${MISSING} — ${reason}` : undefined} tabIndex={reason ? 0 : undefined}>{MISSING}</span>;
}

/** Apply missing/defect ink at the display boundary without changing formatter strings or accessible labels. */
export function displayValue(nodes: ReactNode): ReactNode {
  return Children.map(nodes, node => {
    if (node === MISSING || node === "—") return <NA />;
    if (typeof node === "string" && INVALID_VALUES.has(node)) return <span className="text-bad">{node}</span>;
    if (!isValidElement<{ children?: ReactNode; className?: string }>(node) || node.type === NA || node.props.className?.split(" ").includes("na")) return node;
    return node.props.children ? cloneElement(node, { children: displayValue(node.props.children) }) : node;
  });
}

export function KV({ items, wide = [] }: { items: [string, ReactNode][]; wide?: string[] }) {
  return (
    <dl className="kv">
      {items.map(([k, v]) => (
        <div key={k} className={wide.includes(k) ? "kv__wide" : undefined}>
          <dt>{k}</dt>
          <dd>{v == null ? <NA /> : displayValue(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function scopedTableHead(nodes: ReactNode): ReactNode {
  return Children.map(nodes, node => {
    if (!isValidElement<{ children?: ReactNode; scope?: string }>(node)) return node;
    return cloneElement(node, {
      ...(node.type === "th" ? { scope: node.props.scope ?? "col" } : {}),
      ...(node.props.children ? { children: scopedTableHead(node.props.children) } : {}),
    });
  });
}

function tableColumnLabels(nodes: ReactNode): ReactNode[] {
  return Children.toArray(nodes).flatMap(node => isValidElement<{ children?: ReactNode; "aria-label"?: string }>(node)
    ? node.type === "th" ? [node.props["aria-label"] ?? node.props.children] : tableColumnLabels(node.props.children) : []);
}
function labelledTableRows(nodes: ReactNode, labels: ReactNode[]): ReactNode {
  return Children.map(nodes, node => {
    if (!isValidElement<{ children?: ReactNode }>(node)) return node;
    if (node.type !== "tr") return node.props.children ? cloneElement(node, { children: labelledTableRows(node.props.children, labels) }) : node;
    let column = 0;
    return cloneElement(node, { children: Children.map(node.props.children, cell => {
      if (!isValidElement<{ children?: ReactNode; colSpan?: number }>(cell) || (cell.type !== "td" && cell.type !== "th")) return cell;
      const label = labels[column]; column += cell.props.colSpan ?? 1;
      if (cell.type === "th") return cell;
      return cloneElement(cell, { children: <>{!cell.props.colSpan && label && <span className="tbl__cell-label" aria-hidden="true">{label}</span>}<div className="tbl__cell-value">{cell.props.children}</div></> });
    }) });
  });
}

export function Table({ label, head, children, busy = false, caption, mobile = "scroll" }: { mobile?: "scroll" | "stack"; caption?: string | undefined; label: string; head: ReactNode; children: ReactNode; busy?: boolean }) {
  const region = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLTableSectionElement>(null);
  useLayoutEffect(() => {
    const element = heading.current;
    if (!element) return;
    const measure = () => region.current?.style.setProperty("--table-head-height", `${element.getBoundingClientRect().height}px`);
    measure(); const observer = new ResizeObserver(measure); observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const columns = isValidElement<{children: ReactNode}>(head) ? Children.count(head.props.children) : 1;
  return (
    <div ref={region} className="card scroll" role="region" aria-label={label} aria-busy={busy} tabIndex={0}>
      <table className={`tbl${mobile === "stack" ? " tbl--stack" : ""}`}>
        <caption className={caption ? undefined : "sr-only"}>{caption ?? label}</caption>
        <thead ref={heading}>{scopedTableHead(head)}</thead>
        <tbody>{busy ? Array.from({ length: 3 }, (_, row) => <tr key={row}>{Array.from({length: columns}, (_, col) => <td key={col}><div className="skeleton__cell" /></td>)}</tr>) : labelledTableRows(displayValue(children), tableColumnLabels(head))}</tbody>
      </table>
    </div>
  );
}

/** Route navigation shares one current-page treatment; it is not an ARIA tab widget. */
export function SectionNav({ label, links }: { label: string; links: { label: string; href: string; current: boolean }[] }) {
  return <nav className="tabs tabs--page" aria-label={label}>{links.map((link) => <a key={link.href} href={link.href} aria-current={link.current ? "page" : undefined}>{link.label}</a>)}</nav>;
}

export function Banner({ error, onRetry }: { error: AppError | Error | null | undefined; onRetry?: (() => void) | undefined }) {
  const alert = useRef<HTMLDivElement>(null);
  useEffect(() => { if (error && alert.current?.closest("[role=dialog]")) alert.current.focus(); }, [error]);
  if (!error) return null;
  const e = error instanceof AppError ? error : null;
  // Problem detail is diagnostic, not presentation logic (§2.5). The server's
  // remedy is the stable, policy-owned prose; never regex-match a tool identifier.
  // The refusal code picks a sentence and is never shown; the request id is, as the
  // Correlation ID the person can quote (DS §2.1).
  const fallback: Record<string, string> = {
    NOT_ROLE: "This action is not available to your role.",
    NOT_FOUND: "We can't show that record.",
    NOT_FOUND_OR_NOT_PERMITTED: "We can't show that record.",
    SOD_CONFLICT: "A different Admin must approve this draft.",
    NETWORK: "The application is not reachable right now. Try again when your connection is restored.",
  };
  const human = e ? e.remedy || fallback[e.code] || "This action could not be completed. Review the requirements and try again." : "The application could not complete this action. Please try again.";
  const retryable = !e || e.status === 0 || e.status >= 500;
  return <div className="callout callout--bad" role="alert" tabIndex={-1} ref={alert}>
    <p style={{ margin: 0 }}>{human}</p>
    {e?.requestId && <div className="muted">{ERROR_LABELS.correlationId} <span className="mono">{e.requestId}</span></div>}
    {onRetry && retryable && <button type="button" className="btn btn--sm" onClick={onRetry}>Retry</button>}
  </div>;
}

export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="skeleton" role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton__row" />
      ))}
    </div>
  );
}

export function Empty({ children, heading, action, level = 3 }: { children: ReactNode; heading: string; action?: ReactNode; level?: 2 | 3 }) {
  const Heading = level === 2 ? "h2" : "h3";
  return <div className="empty">{heading && <Heading>{heading}</Heading>}<div>{children}</div>{action && <div className="empty__action">{action}</div>}</div>;
}

export function Receipt({ children }: { children: ReactNode }) {
  return (
    <div className="callout callout--ok" role="status">
      {children}
    </div>
  );
}

/** Modal reason-and-confirm dialog (§1.5): labelled, focus trapped, Escape cancels, returns focus to the opener. */
const FOCUSABLE = 'a[href],area[href],input:not([type="hidden"]),select,textarea,button,iframe,object,embed,audio[controls],video[controls],summary,[contenteditable]:not([contenteditable="false"]),[tabindex]';
export function focusable(root: ParentNode | null, scope?: string): HTMLElement[] {
  const within = scope ? root?.querySelector(scope) : root;
  return Array.from(within?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter(node =>
    node.tabIndex >= 0 && !node.matches(":disabled") && !node.closest("[inert],[hidden]") &&
    node.getClientRects().length > 0 && !["hidden", "collapse"].includes(getComputedStyle(node).visibility));
}

// Only the top dialog owns modal semantics; background content cannot receive focus.
const dialogStack: HTMLElement[] = [];
let rootWasInert = false;
function syncDialogStack() {
  const top = dialogStack.at(-1);
  for (const dialog of dialogStack) {
    dialog.inert = dialog !== top;
    dialog.setAttribute("aria-modal", String(dialog === top));
  }
  const root = document.getElementById("root");
  if (root) root.inert = top ? true : rootWasInert;
}

export function Dialog({ title, open, onClose, children, titleAside, focusTitle = false }: { titleAside?: ReactNode; title: string; open: boolean; onClose: () => void; children?: ReactNode; focusTitle?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const id = useId();
  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement;
    const element = ref.current!;
    if (!dialogStack.length) rootWasInert = document.getElementById("root")?.inert ?? false;
    dialogStack.push(element); syncDialogStack();
    const first = focusTitle ? ref.current?.querySelector<HTMLElement>(".dialog__title") : focusable(ref.current, ".dialog__body")[0];
    (first ?? ref.current?.querySelector<HTMLElement>(".dialog__close"))?.focus();
    function onKey(e: KeyboardEvent) {
      if (document.querySelectorAll("[role=dialog]").item(document.querySelectorAll("[role=dialog]").length - 1) !== ref.current) return;
      if (e.key === "Escape") close.current();
      if (e.key === "Tab" && ref.current) {
        const f = focusable(ref.current);
        if (f.length === 0) return;
        const firstEl = f[0]!;
        const lastEl = f[f.length - 1]!;
        if (e.shiftKey && (document.activeElement === firstEl || document.activeElement === ref.current.querySelector(".dialog__title"))) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const index = dialogStack.indexOf(element);
      if (index >= 0) dialogStack.splice(index, 1);
      element.inert = false; syncDialogStack();
      (opener.current as HTMLElement | null)?.focus?.();
    };
  }, [open, focusTitle]);
  if (!open) return null;
  return createPortal(
    <div className="dialog-scrim" onClick={onClose}>
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby={id} ref={ref} onClick={(e) => e.stopPropagation()}>
        <div className="dialog__header"><h2 id={id} className="dialog__title" tabIndex={focusTitle ? -1 : undefined}>{title}</h2>{titleAside}<button type="button" className="dialog__close" aria-label="Close" onClick={onClose}>×</button></div>
        <div className="dialog__body">{children}</div>
      </div>
    </div>, document.body
  );
}

export function RequiredMark() {
  return <span className="req" aria-hidden="true"> ● Required</span>;
}

type FieldControlProps = { id?: string; type?: string; children?: ReactNode; required?: boolean; value?: unknown; defaultValue?: unknown; "aria-required"?: boolean | "true" | "false"; "aria-invalid"?: boolean | "true" | "false" | "grammar" | "spelling"; "aria-describedby"?: string };
function firstFieldControl(nodes: ReactNode): string | null | undefined {
  for (const node of Children.toArray(nodes)) {
    if (!isValidElement<FieldControlProps>(node)) continue;
    if (["input", "select", "textarea"].includes(String(node.type)) && node.props.type !== "hidden") return node.props.id ?? null;
    const nested = firstFieldControl(node.props.children);
    if (nested !== undefined) return nested;
  }
  return undefined;
}

const FieldErrorsContext = createContext<Record<string, string>>({});

export function Field({ label, required, hint, error, children, id }: { label: string; required?: boolean | undefined; hint?: ReactNode; error?: string | undefined; children: ReactNode; id?: string | undefined }) {
  const autoId = useId();
  const fid = id ?? firstFieldControl(children) ?? autoId;
  const validationErrors = useContext(FieldErrorsContext);
  error = error ?? validationErrors[fid];
  let bound = false;
  const bind = (nodes: ReactNode): ReactNode => Children.map(nodes, child => {
    if (!isValidElement<FieldControlProps>(child)) return child;
    const native = ["input", "select", "textarea"].includes(String(child.type)) && child.props.type !== "hidden";
    if (native && !bound && (!child.props.id || child.props.id === fid)) {
      bound = true;
      const needed = required ?? child.props.required ?? false;
      const descriptions = [...new Set([...(child.props["aria-describedby"]?.split(/\s+/) ?? []), hint ? `${fid}-hint` : "", error ? `${fid}-err` : ""].filter(Boolean))].join(" ");
      const control = cloneElement(child, { id: fid, required: needed, "aria-required": needed, "aria-invalid": error ? true : child.props["aria-invalid"] ?? false, ...(descriptions ? { "aria-describedby": descriptions } : {}) });
      return child.type === "textarea" ? <div className="print-expand" data-value={String(child.props.value ?? child.props.defaultValue ?? "")}>{control}</div> : control;
    }
    return child.props.children ? cloneElement(child, { children: bind(child.props.children) }) : child;
  });
  return (
    <div className={"field" + (error ? " field--error" : "")}>
      <div className="field__label"><label htmlFor={fid}>{label}</label>{required && <RequiredMark />}</div>
      {bind(children)}
      {hint && (
        <div className="field__hint" id={`${fid}-hint`}>
          {hint}
        </div>
      )}
      {error && (
        <div className="field__error" id={`${fid}-err`}>
          {error}
        </div>
      )}
    </div>
  );
}

/** A reason dialog validates all of its fields before sending the governed write. */
export function ReasonDialog({ title, label = "Reason", confirm = "Confirm", open, onClose, onConfirm, busy, error, children, optional = false, validateOnSubmit = false, blockedReason, preview }: { title: string; label?: string | undefined; confirm?: string | undefined; open: boolean; onClose: () => void; onConfirm: (reason: string) => void; busy?: boolean | undefined; error?: AppError | Error | null | undefined; children?: ReactNode; optional?: boolean | undefined; validateOnSubmit?: boolean; blockedReason?: string | undefined; preview?: (reason: string) => ReactNode }) {
  const min = optional ? 0 : 3;
  const [dirty, setDirty] = useState(false);
  const guard = useUnsavedGuard(open && dirty, title, () => setDirty(false));
  const close = () => guard.confirm(onClose);
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<{ id: string; label: string; message: string }[]>([]);
  const [attempt, setAttempt] = useState(0);
  const summary = useRef<HTMLDivElement>(null);
  const [submittedReason, setSubmittedReason] = useState<string | null>(null);
  const confirming = useRef(false);
  useLayoutEffect(() => { if (!busy) confirming.current = false; }, [open, busy, error]);
  const fieldId = useId();
  const refused = error instanceof AppError && [403, 404, 409, 422].includes(error.status);
  const unchangedRefusal = refused && submittedReason === reason;
  const disabled = !!busy || !!blockedReason || unchangedRefusal || !validateOnSubmit && reason.trim().length < min;
  useEffect(() => {
    if (open) { setDirty(false); setReason(""); setErrors([]); setAttempt(0); setSubmittedReason(null); }
  }, [open]);
  useLayoutEffect(() => { if (attempt) summary.current?.focus(); }, [attempt]);
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (confirming.current || busy || blockedReason || unchangedRefusal) return;
    const invalid = [...e.currentTarget.elements].filter((node): node is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement =>
      (node instanceof HTMLInputElement || node instanceof HTMLSelectElement || node instanceof HTMLTextAreaElement) && node.willValidate && !node.validity.valid
    ).map(node => ({ id: node.id, label: node.labels?.[0]?.textContent ?? node.getAttribute("aria-label") ?? "Field", message: node.validationMessage }));
    if (reason.trim().length < min) {
      const message = `${label} must contain at least three characters.`;
      const existing = invalid.find(item => item.id === fieldId);
      if (existing) existing.message = message; else invalid.push({ id: fieldId, label, message });
    }
    setErrors(invalid);
    if (invalid.length) { setAttempt(value => value + 1); return; }
    confirming.current = true;
    setSubmittedReason(reason);
    try { onConfirm(reason.trim()); } catch (error) { confirming.current = false; throw error; }
  }
  return (
    <><Dialog title={title} open={open} onClose={close}>
      <FieldErrorsContext.Provider value={Object.fromEntries(errors.map(item => [item.id, item.message]))}>
      <form onSubmit={submit} noValidate onChange={event => { setDirty(true); setSubmittedReason(null); const id = (event.target as HTMLElement).id; setErrors(items => items.filter(item => item.id !== id)); }}>
        {errors.length > 0 && <div ref={summary} role="alert" tabIndex={-1} className="callout callout--bad form-errors"><p>Check the following fields:</p><ul>{errors.map(item => <li key={item.id}><a href={`#${item.id}`} onClick={event => { event.preventDefault(); document.getElementById(item.id)?.focus(); }}>{item.label}: {item.message}</a></li>)}</ul></div>}
        {children}
        <Field label={label} required={!optional} id={fieldId} hint={!optional ? `${label} must contain at least three characters.` : undefined}>
          <textarea id={fieldId} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} required={!optional} minLength={min || undefined} maxLength={1000} />
        </Field>
        {preview?.(reason.trim())}
        {blockedReason && <p id={`${fieldId}-blocked`} className="muted" role="status">{blockedReason}</p>}
        <div id={`${fieldId}-refusal`}><Banner error={error} /></div>
        <div className="row row--end" style={{ gap: 8, marginTop: 8 }}>
          <button type="button" className="btn" onClick={close}>Cancel</button>
          <button type="submit" className={refused ? "btn" : "btn btn--primary"} aria-disabled={disabled} disabled={disabled} aria-describedby={[error ? `${fieldId}-refusal` : "", blockedReason ? `${fieldId}-blocked` : "", !optional && !error && !blockedReason ? `${fieldId}-hint` : ""].filter(Boolean).join(" ") || undefined}>
            {busy ? "Saving…" : confirm}
          </button>
        </div>
      </form>
      </FieldErrorsContext.Provider>
    </Dialog>{guard.dialog}</>
  );
}

/** `2026-11-02` — a CALENDAR DATE, which names a day and no instant at all. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function fmtDate(v: string | null | undefined, withTime = false, zone = getBusinessTimeZone()): string {
  if (!v) return MISSING;
  // A date-only value (a start date, a target start, an effective date) is a day, not a moment: read as an instant it
  // becomes midnight UTC and shows as the day BEFORE in a western business zone — "2026-11-02" printed as "Nov 1"
  // (Codex #9 M13). It is formatted as the day it names, in no zone, and carries no time even when one is asked for.
  const cal = DATE_ONLY.exec(v);
  if (cal) {
    const [y, m, d0] = [Number(cal[1]), Number(cal[2]), Number(cal[3])];
    const day = new Date(Date.UTC(y, m - 1, d0));
    // a day that does not exist (2026-02-31) rolls over into another date: it is malformed evidence, not an absent value
    if (Number.isNaN(day.getTime()) || day.getUTCFullYear() !== y || day.getUTCMonth() !== m - 1 || day.getUTCDate() !== d0) return INVALID_DATE;
    return day.toLocaleDateString(undefined, { dateStyle: "medium", timeZone: "UTC" });
  }
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return INVALID_DATE; // Malformed evidence stays distinct from an absent value.
  const timeZone = zone;
  // dateStyle/timeStyle cannot be combined with timeZoneName, so the zone label is formatted separately
  return withTime
    ? `${d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short", timeZone })} ${zoneLabel(d, timeZone)}`
    : d.toLocaleDateString(undefined, { dateStyle: "medium", timeZone });
}

/** Compact history age; the absolute business-zone instant remains in the caller's title. */
export function relativeAge(value: string): string {
  if (!value) return MISSING;
  const milliseconds = Date.parse(value) - Date.now();
  if (!Number.isFinite(milliseconds)) return INVALID_DATE;
  const seconds = Math.round(milliseconds / 1000);
  const scale: [Intl.RelativeTimeFormatUnit, number] = Math.abs(seconds) < 60 ? ["second", 1] : Math.abs(seconds) < 3600 ? ["minute", 60] : Math.abs(seconds) < 86400 ? ["hour", 3600] : ["day", 86400];
  return new Intl.RelativeTimeFormat(undefined, { numeric: "auto" }).format(Math.round(seconds / scale[1]), scale[0]);
}

/** One complete range in the same business zone as fmtDate; no slicing locale-dependent words. */
export function fmtRange(start: string, end: string, timeZone = getBusinessTimeZone()): string {
  if (!start || !end) return MISSING;
  const a = new Date(start), b = new Date(end);
  if (!Number.isFinite(a.getTime()) || !Number.isFinite(b.getTime()) || b < a) return INVALID_TIME;
  return new Intl.DateTimeFormat(undefined, { timeZone, weekday: "short", year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }).formatRange(a, b);
}
export function fmtTime(value: string | null | undefined): string {
  if (!value) return MISSING;
  if (!Number.isFinite(Date.parse(value))) return INVALID_DATE;
  return new Intl.DateTimeFormat(undefined, { timeZone: getBusinessTimeZone(), hour: "numeric", minute: "2-digit" }).format(new Date(value));
}
export const ISO_IN_TEXT = /\d{4}-\d{2}-\d{2}T[^\s,;)]+/g;
export function humanizeDates(value: string): string {
  return value.replace(ISO_IN_TEXT, (instant) => fmtDate(instant, true));
}

function zoneLabel(d: Date, timeZone: string): string {
  try {
    const part = new Intl.DateTimeFormat(undefined, { timeZone, timeZoneName: "short" }).formatToParts(d).find((p) => p.type === "timeZoneName");
    return part?.value ?? timeZone;
  } catch {
    return timeZone; // an unknown zone name still labels the time honestly instead of taking the screen down
  }
}

export function fmtMoney(v: string | number | null | undefined, ccy = "USD"): string {
  if (v === null || v === undefined || v === "") return MISSING;
  if (!Number.isFinite(Number(v))) return INVALID_AMOUNT;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(Number(v));
  } catch { return INVALID_AMOUNT; }
}

/** A sentence-case label for server-projected evidence; enum values keep their shared vocabulary. */
export const recordLabel = (key: string): string => {
  const labels: Record<string, string> = { req_no: "Requisition number", openings_approved: "Approved openings", taken_by: "Archived by", phase_key: "Phase", pinned_config: "Pinned configuration" };
  const words = (labels[key] ?? roleLabel(key)).replace(/_/g, " ").replace(/\bid\b/g, "ID").replace(/\bsha256\b/g, "SHA-256");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Compact evidence is the default; retained archive documents opt into sibling sections. */
export function RecordFields({ value, variant = "compact" }: { value: unknown; variant?: "compact" | "document" }) {
  if (value == null) return <NA />;
  if (typeof value === "boolean") return <span>{value ? "Yes" : "No"}</span>;
  if (typeof value !== "object") return <span className="long">{displayValue(typeof value === "string" && /^\d{4}-\d\d-\d\d(?:T\d\d:|$)/.test(value) ? fmtDate(value, value.includes("T")) : roleLabel(String(value)))}</span>;
  if (variant === "compact" && Array.isArray(value) && value.length === 1) return <RecordFields variant={variant} value={value[0]} />;
  if (Array.isArray(value)) return value.length ? <ol className="admin-record-list">{value.map((v, i) => <li key={i} value={i + 1}><RecordFields variant={variant} value={v} /></li>)}</ol> : <span className="muted">None</span>;
  if (Object.keys(value).length === 0) return <span className="muted">None</span>;
  const entries = Object.entries(value);
  if (variant === "compact") {
    const fields: [string, ReactNode][] = [];
    const flatten = (child: unknown, path: string[]) => {
      if (child != null && typeof child === "object" && Object.keys(child).length) {
        Object.entries(child).forEach(([key, nested]) => flatten(nested, [...path, Array.isArray(child) ? `Item ${Number(key) + 1}` : recordLabel(key)]));
      } else {
        fields.push([path.join(" · "), child != null && typeof child === "object" ? <span className="muted">None</span> : <RecordFields value={child} />]);
      }
    };
    entries.forEach(([key, child]) => flatten(child, [recordLabel(key)]));
    return <div className="admin-fields"><KV items={fields} /></div>;
  }
  const fields = entries.filter(([key, v]) => key !== "token" && (v == null || typeof v !== "object"));
  const groups = entries.filter(([key]) => key !== "token" && !fields.some(([field]) => field === key));
  return <div className="admin-fields">
    {"token" in value && <div className="sub mono">{String(value.token)}</div>}
    {fields.length > 0 && <KV wide={fields.filter(([key]) => /hash|sha256/.test(key)).map(([key]) => recordLabel(key))} items={fields.map(([k, v]) => [recordLabel(k), Array.isArray(v) ? v.length ? v.map((child, i) => <span key={i}>{i > 0 ? " · " : ""}<RecordFields variant={variant} value={child} /></span>) : <span className="muted">None</span> : <RecordFields variant={variant} value={v} />])} />}
    {groups.map(([key, child]) => <section className="record-section" key={key}><div className="sub">{recordLabel(key)}</div><RecordFields variant={variant} value={child} /></section>)}
  </div>;
}

export function Pager({ next, previous, busy, count, page = 1 }: { count?: number; page?: number; next?: (() => void) | undefined; previous?: (() => void) | undefined; busy?: boolean | undefined }) {
  if (count === 0 && !next && !previous) return null;
  return <div className="acts admin-pager">{count !== undefined && <span role="status">Showing {count} {count === 1 ? "record" : "records"} · page {page}</span>}<button className="btn btn--sm" disabled={!previous || busy} aria-disabled={!previous || busy} onClick={previous}>Previous page</button><button className="btn btn--sm" disabled={!next || busy} aria-disabled={!next || busy} onClick={next}>Next page</button></div>;
}

export function downloadText(name: string, content: string, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement("a"); link.href = url; link.download = name; link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const roleLabel = (key: string): string => ({ ...LEVEL_LABEL, ...STAGE_LABEL, hm: "Hiring Manager", hrbp: "HR Business Partner", recruiter: "Recruiter", interviewer: "Interviewer", admin: "Admin", system: "System", coordinator: "Coordinator", ta_manager: "TA Manager", compensation: "Compensation", ta_admin: "TA Admin (retired)", hr_sys_admin: "HR Systems Admin (retired)", hr_ops: "HR Operations (retired)", resume: "Résumé", packet_pdf: "Interview packet", import_csv: "Candidate import", attachment: "Attachment", quarantined: "Quarantined", scanned_clean: "Ready to attach", rejected: "Rejected", abandoned: "Abandoned", intake_pdf: "Intake document", paper_scorecard: "Paper scorecard", exercise_return: "Exercise return", written_input: "Written input", summary_export: "Summary Sheet export", posting_to_sourcing: "Posting to sourcing", sourcing_to_screen: "Sourcing to screen", screen_to_hm: "Screen to Hiring Manager", hm_to_panel: "Hiring Manager to panel", panel_to_debrief: "Panel to debrief" })[key] ?? key;
export const personLabel = (name?: string | null, id?: string | null): string => name ? `${name}${id ? ` · ${id}` : ""}` : id ?? "—";
